import React, {useState} from 'react';
import {
    Camera,
    Clock,
    Edit2,
    Eye,
    Mail,
    MapPin,
    MessageCircle,
    Package,
    Phone,
    Plus,
    Share2,
    User,
    Wrench,
    X
} from 'lucide-react';
import {Header} from "../components/Header";

export const YellowPageEntityProfile = () => {
    const [activeTab, setActiveTab] = useState('profile');
    const [editMode, setEditMode] = useState(false);
    const [showAddProduct, setShowAddProduct] = useState(false);
    const [showAddService, setShowAddService] = useState(false);

    // Sample user data based on the registration form
    const [profileData, setProfileData] = useState({
        ownerName: "Rajesh Kumar",
        businessName: "Kumar Electronics & Repair",
        entityType: "Business Entity",
        offerType: "Both", // Products and Services
        category: "Electronics & Electrical",
        mobile: "+91 98765 43210",
        email: "kumar.electronics@gmail.com",
        whatsapp: "+91 98765 43210",
        address: "Shop No. 15, Main Market, Near City Hospital",
        area: "Sector 21",
        pinCode: "400708",
        description: "We sell mobile phones, accessories, and electronic gadgets. Also provide repair services for all brands of smartphones and electronic devices.",
        experience: "5-10 years",
        businessHours: "9:00 AM - 9:00 PM",
        photos: ["/assets/tech-filler-1.png", "/assets/tech-filler-2.png", "/assets/tech-filler-3.png"]
    });

    const [products, setProducts] = useState([
        {
            id: 1,
            name: "Samsung Galaxy Smartphones",
            price: "₹15,000 - ₹80,000",
            description: "Latest Samsung Galaxy series phones with warranty",
            image: "/assets/tech-filler-1.png",
            inStock: true
        },
        {
            id: 2,
            name: "Mobile Accessories",
            price: "₹100 - ₹2,000",
            description: "Phone cases, chargers, earphones, screen protectors",
            image: "/assets/tech-filler-2.png",
            inStock: true
        }
    ]);

    const [services, setServices] = useState([
        {
            id: 1,
            name: "Mobile Phone Repair",
            price: "₹500 - ₹3,000",
            description: "Screen replacement, battery change, software issues",
            duration: "1-2 hours",
            image: "/assets/tech-filler-1.png"
        },
        {
            id: 2,
            name: "Electronic Device Servicing",
            price: "₹300 - ₹1,500",
            description: "Repair of laptops, tablets, and small electronic devices",
            duration: "2-24 hours",
            image: "/assets/tech-filler-2.png"
        }
    ]);

    const ProfileHeader = () => (
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-t-lg">
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    <h1 className="text-2xl font-custom-bold mb-2">{profileData.businessName}</h1>
                    <div className="flex items-center gap-2 mb-2">
                        <User size={16}/>
                        <span>Owner: {profileData.ownerName}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                        <MapPin size={16}/>
                        <span>{profileData.area}, {profileData.pinCode}</span>
                    </div>
                    <span className="bg-blue-500 px-3 py-1 rounded-full text-sm">
            {profileData.category}
          </span>
                </div>
                <div className="text-right">
                    <button
                        onClick={() => setEditMode(!editMode)}
                        className="bg-white text-blue-600 px-4 py-2 rounded-lg flex items-center gap-2 mb-2 hover:bg-gray-100"
                    >
                        <Edit2 size={16}/>
                        {editMode ? 'Cancel' : 'Edit Profile'}
                    </button>
                    <div className="flex gap-2">
                        <button className="bg-blue-500 p-2 rounded-lg hover:bg-blue-400">
                            <Share2 size={16}/>
                        </button>
                        <button className="bg-blue-500 p-2 rounded-lg hover:bg-blue-400">
                            <Eye size={16}/>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const ContactInfo = () => (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="font-custom-semi-bold mb-3">Contact Information</h3>
            <div className="space-y-2">
                <div className="flex items-center gap-3">
                    <Phone size={16} className="text-green-600"/>
                    <span>{profileData.mobile}</span>
                </div>
                {profileData.email && (
                    <div className="flex items-center gap-3">
                        <Mail size={16} className="text-blue-600"/>
                        <span>{profileData.email}</span>
                    </div>
                )}
                <div className="flex items-center gap-3">
                    <MessageCircle size={16} className="text-green-500"/>
                    <span>WhatsApp: {profileData.whatsapp}</span>
                </div>
                <div className="flex items-center gap-3">
                    <Clock size={16} className="text-gray-600"/>
                    <span>{profileData.businessHours}</span>
                </div>
            </div>
        </div>
    );

    const BusinessDetails = () => (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="font-custom-semi-bold mb-3">Business Details</h3>
            <div className="space-y-3">
                <div>
                    <label className="text-sm font-custom-medium text-gray-600">Description</label>
                    <p className="mt-1">{profileData.description}</p>
                </div>
                <div>
                    <label className="text-sm font-custom-medium text-gray-600">Experience</label>
                    <p className="mt-1">{profileData.experience}</p>
                </div>
                <div>
                    <label className="text-sm font-custom-medium text-gray-600">Complete Address</label>
                    <p className="mt-1">{profileData.address}</p>
                </div>
            </div>
        </div>
    );

    const PhotoGallery = () => (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="font-custom-semi-bold mb-3">Photos</h3>
            <div className="grid grid-cols-3 gap-2">
                {profileData.photos.map((photo, index) => (
                    <img
                        key={index}
                        src={photo}
                        alt={`Business photo ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                    />
                ))}
                {editMode && (
                    <div
                        className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-50">
                        <Camera size={24} className="text-gray-400"/>
                    </div>
                )}
            </div>
        </div>
    );

    const ProductsSection = () => (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-custom-semi-bold">Products</h2>
                <button
                    onClick={() => setShowAddProduct(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                >
                    <Plus size={16}/>
                    Add Product
                </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product) => (
                    <div key={product.id} className="bg-white border rounded-lg overflow-hidden shadow-sm">
                        <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-32 object-cover"
                        />
                        <div className="p-4">
                            <h3 className="font-custom-semi-bold mb-1">{product.name}</h3>
                            <p className="text-blue-600 font-custom-medium mb-2">{product.price}</p>
                            <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                            <div className="flex justify-between items-center">
                <span className={`px-2 py-1 rounded-full text-xs ${
                    product.inStock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {product.inStock ? 'In Stock' : 'Out of Stock'}
                </span>
                                <button className="text-blue-600 hover:text-blue-800">
                                    <Edit2 size={14}/>
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const ServicesSection = () => (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-custom-semi-bold">Services</h2>
                <button
                    onClick={() => setShowAddService(true)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
                >
                    <Plus size={16}/>
                    Add Service
                </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((service) => (
                    <div key={service.id} className="bg-white border rounded-lg overflow-hidden shadow-sm">
                        <img
                            src={service.image}
                            alt={service.name}
                            className="w-full h-32 object-cover"
                        />
                        <div className="p-4">
                            <h3 className="font-custom-semi-bold mb-1">{service.name}</h3>
                            <p className="text-green-600 font-custom-medium mb-1">{service.price}</p>
                            <p className="text-sm text-gray-500 mb-2">Duration: {service.duration}</p>
                            <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                            <button className="text-green-600 hover:text-green-800">
                                <Edit2 size={14}/>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const AddProductModal = () => (
        showAddProduct && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-custom-semi-bold">Add New Product</h3>
                        <button
                            onClick={() => setShowAddProduct(false)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <X size={20}/>
                        </button>
                    </div>
                    <form className="space-y-4">
                        <div>
                            <label className="block text-sm font-custom-medium mb-1">Product Name*</label>
                            <input
                                type="text"
                                className="w-full border rounded-lg px-3 py-2"
                                placeholder="Enter product name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-custom-medium mb-1">Price Range*</label>
                            <input
                                type="text"
                                className="w-full border rounded-lg px-3 py-2"
                                placeholder="₹100 - ₹1,000"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-custom-medium mb-1">Description*</label>
                            <textarea
                                className="w-full border rounded-lg px-3 py-2"
                                rows="3"
                                placeholder="Describe your product"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-custom-medium mb-1">Product Image</label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                <Camera size={24} className="mx-auto text-gray-400 mb-2"/>
                                <p className="text-sm text-gray-500">Click to upload image</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowAddProduct(false)}
                                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                            >
                                Add Product
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )
    );

    const AddServiceModal = () => (
        showAddService && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-custom-semi-bold">Add New Service</h3>
                        <button
                            onClick={() => setShowAddService(false)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <X size={20}/>
                        </button>
                    </div>
                    <form className="space-y-4">
                        <div>
                            <label className="block text-sm font-custom-medium mb-1">Service Name*</label>
                            <input
                                type="text"
                                className="w-full border rounded-lg px-3 py-2"
                                placeholder="Enter service name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-custom-medium mb-1">Price Range*</label>
                            <input
                                type="text"
                                className="w-full border rounded-lg px-3 py-2"
                                placeholder="₹500 - ₹2,000"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-custom-medium mb-1">Duration</label>
                            <input
                                type="text"
                                className="w-full border rounded-lg px-3 py-2"
                                placeholder="1-2 hours"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-custom-medium mb-1">Description*</label>
                            <textarea
                                className="w-full border rounded-lg px-3 py-2"
                                rows="3"
                                placeholder="Describe your service"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-custom-medium mb-1">Service Image</label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                <Camera size={24} className="mx-auto text-gray-400 mb-2"/>
                                <p className="text-sm text-gray-500">Click to upload image</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowAddService(false)}
                                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
                            >
                                Add Service
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )
    );

    return (
        <div
            className={`min-h-[100dvh] h-[100dvh] max-h-[100dvh]
                min-w-full w-full max-w-full 
                bg-white flex flex-col justify-end`}
        >
            {/* Header */}
            <Header/>

            {/* Main Content */}
            <div
                className="bg-orange-50 font-custom-sans text-gray-800 min-h-screen
                    flex flex-col justify-between thin-scrollbar overflow-y-scroll min-w-full w-full max-w-full

                    portrait:pt-[4.75rem] portrait:sm:pt-[6.25rem] portrait:jk:pt-[5.75rem]

                    portrait:min-h-[100dvh] portrait:h-[100dvh] portrait:max-h-[100dvh]

                    landscape:pt-[3.5rem] landscape:jk:pt-[4.5rem]

                    landscape:min-h-[100dvh] landscape:h-[100dvh] landscape:max-h-[100dvh]

                    portrait:pb-[0.75rem] portrait:pl-[0.75rem] portrait:pr-[0.75rem]
                    landscape:pb-[0.5rem] landscape:pl-[0.5rem] landscape:pr-[0.5rem]

                    portrait:gap-[0.75rem] landscape:gap-[0.5rem]"
            >
                <div className="max-w-6xl mx-auto p-4">
                    {/* Profile Header */}
                    <div className="mb-6">
                        <ProfileHeader/>
                    </div>

                    {/* Tab Navigation */}
                    <div className="bg-white rounded-lg shadow-sm mb-6">
                        <div className="border-b border-gray-200">
                            <nav className="flex">
                                <button
                                    onClick={() => setActiveTab('profile')}
                                    className={`px-6 py-3 font-custom-medium ${
                                        activeTab === 'profile'
                                            ? 'border-b-2 border-blue-600 text-blue-600'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <User size={16} className="inline mr-2"/>
                                    Profile Details
                                </button>
                                <button
                                    onClick={() => setActiveTab('products')}
                                    className={`px-6 py-3 font-custom-medium ${
                                        activeTab === 'products'
                                            ? 'border-b-2 border-blue-600 text-blue-600'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <Package size={16} className="inline mr-2"/>
                                    Products ({products.length})
                                </button>
                                <button
                                    onClick={() => setActiveTab('services')}
                                    className={`px-6 py-3 font-custom-medium ${
                                        activeTab === 'services'
                                            ? 'border-b-2 border-blue-600 text-blue-600'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <Wrench size={16} className="inline mr-2"/>
                                    Services ({services.length})
                                </button>
                            </nav>
                        </div>

                        <div className="p-6">
                            {activeTab === 'profile' && (
                                <div className="grid lg:grid-cols-3 gap-6">
                                    <div className="lg:col-span-2 space-y-6">
                                        <BusinessDetails/>
                                        <PhotoGallery/>
                                    </div>
                                    <div>
                                        <ContactInfo/>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'products' && <ProductsSection/>}
                            {activeTab === 'services' && <ServicesSection/>}
                        </div>
                    </div>

                    {/* Statistics Card */}
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <h3 className="font-custom-semi-bold mb-3">Profile Statistics</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-custom-bold text-blue-600">127</div>
                                <div className="text-sm text-gray-600">Profile Views</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-custom-bold text-green-600">23</div>
                                <div className="text-sm text-gray-600">Contact Clicks</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-custom-bold text-orange-600">4.5</div>
                                <div className="text-sm text-gray-600">Rating</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-custom-bold text-purple-600">8</div>
                                <div className="text-sm text-gray-600">Reviews</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Product Modal */}
                <AddProductModal/>

                {/* Add Service Modal */}
                <AddServiceModal/>
            </div>
        </div>
    );
}
